import {PortedVueFormController} from "./ported-vue-forms-helpers";

export type FormData = {
	assign: boolean,
	ip_address: string|null,
	time_zone: string,
	utm_params: {
		utm_source: string|null,
		utm_medium: string|null,
		utm_campaign: string|null,
		utm_term: string|null,
		gclid: string|null
	}
};

export type FormState = {
	data: FormData
}&{[k: string]: string|number|boolean|null};

/**
 * Abstract base class for form controllers
 * Provides common functionality for form handling, submission, cookies, etc.
 */
export abstract class AbstractFormController {
	abstract readonly controller: PortedVueFormController;

	protected response: {body: any, success: boolean} = {
		body: '', success: false
	}

	private readonly nonCacheableDataFetch: Promise<void>;

	protected constructor(
		public readonly container: HTMLDivElement,
		public readonly form: HTMLFormElement
	) {

		this.wireUpDialogs();

		// Fetch non-cacheable form data from API
		this.nonCacheableDataFetch = fetch("/wp-json/homes-for-heroes/v1/non-cacheable-form-data", {method: "POST"})
			.then(resp => resp.json())
			.then(json => {
				for (const [key, value] of Object.entries(json)) {
					this.controller.setFormStateValue(`data.${key}`, value);
				}
				if (document.referrer) {
					this.controller.setFormStateValue("http_referer", document.referrer);
				}
			})
			.catch(err => console.error(err));
		const formCookies = JSON.parse(container.getAttribute("data-form-cookies") ?? "{}");
		for (const [name, value] of Object.entries(formCookies)) {
			const str = value?.toString().trim();
			if (str) {
				this.createCookie(name, str, 30);
			}
		}

		form.addEventListener('submit', e => {
			e.stopPropagation();
			e.preventDefault();
			if (!form.checkValidity()) {
				form.reportValidity();
				return;
			}

			//disable submit button and turn on spinner
			this.setSubmitButtonState(true);
			//end of disable submit button and turn on spinner
			this.handleFormSubmit();
		});

		// Initially disable submit button until CAPTCHA is completed
		this.toggleSubmitButtonDisabledReason("missing-captcha", true);
		form.addEventListener("frc:widget.complete", (event: CustomEventInit<{response?: string}>) => {
			if (event.detail?.response) {
				console.log("Widget was completed! Response:", event.detail?.response);
				this.toggleSubmitButtonDisabledReason("missing-captcha", false);
			} else {
				console.error("Widget ran into an error: no response");
				this.toggleSubmitButtonDisabledReason("missing-captcha", true);
			}
		});

		form.addEventListener("frc:widget.error", (event: CustomEventInit<{error?: string}>) => {
			console.error("Widget ran into an error:", event.detail?.error);
			this.toggleSubmitButtonDisabledReason("missing-captcha", true);
		});

		form.addEventListener("frc:widget.expired", () => {
			console.warn("The widget expired because the user waited too long");
			this.toggleSubmitButtonDisabledReason("missing-captcha", true);
		});

		document.body.dispatchEvent(new CustomEvent('form-attached'));
	}

	protected toggleSubmitButtonDisabledReason(reason: string, applicable: boolean) {
		for (const button of this.form.querySelectorAll<HTMLInputElement|HTMLButtonElement>(".form-group-submit :is(input[type='submit'],button)")) {
			let reasons = button.getAttribute("data-disabled-reasons")?.split(",").map(v => v.trim()).filter(v => v.length > 0) ?? [];
			if (applicable) {
				if (!reasons.includes(reason)) {
					reasons.push(reason);
				}
			} else {
				reasons = reasons.filter(r => r !== reason);
			}
			button.setAttribute("data-disabled-reasons", reasons.join(','));
			button.disabled = reasons.length !== 0;
		}
	}

	/**
	 * Handles form submission process
	 * - Waits for non-cacheable data to be loaded
	 * - Gets CAPTCHA response and sends the form
	 */
	private async handleFormSubmit() {
		await this.nonCacheableDataFetch;
		this.controller.setFormStateValue(
			"frc-captcha-response",
			(this.form.elements.namedItem("frc-captcha-response") as HTMLInputElement|undefined|null)?.value ?? "");
		return this.sendBaseForm();
	}

	/**
	 * Helper method to get a cookie value by key
	 */
	getCookie(key: string) {
		return document.cookie.split(';').find(cookie => cookie.includes(key))?.replace(key + '=', '').trim() ?? "";
	}

	/**
	 * Helper method to create a cookie with optional expiration
	 */
	protected createCookie(name: string, value: string, days?: number) {
		let cookie = `${name}=${value}`;
		if (days) {
			const date = new Date();
			date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
			cookie = `${cookie}; expires=${date.toUTCString()}`;
		}
		document.cookie = `${cookie}; Secure; SameSite=None; path=/`;
	}

	/**
	 * Toggles the submit button state (enabled/disabled) and shows/hides spinner
	 */
	protected setSubmitButtonState(disabled: boolean) {
		for (const button of this.form.querySelectorAll<HTMLButtonElement|HTMLInputElement>("button:not([type='button']),input:where([type='submit'],[type='reset'])")) {
			button.disabled = disabled;
			button.classList.toggle("spinner", disabled);
		}
		this.form.style.pointerEvents = disabled ? "none" : "";
	}

	/**
	 * Collects UTM tracking parameters from session storage or URL parameters
	 */
	storeUTMParameters() {
		const res: Partial<FormData['utm_params']> = {};
		const parsedURLParams = new URL(window.location.href).searchParams;
		for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'gclid'] as Array<keyof FormData['utm_params']>) {
			res[key] = sessionStorage.getItem(key) ?? parsedURLParams.get(key) ?? null;
		}
		return res as FormData['utm_params'];
	}

	/**
	 * Updates UI based on form submission response
	 * - Shows/hides in-progress and complete elements
	 * - Shows error dialog if submission failed
	 */
	protected handleResponse() {
		if (this.response.success) {
			for (const inProgressNode of this.container.querySelectorAll<HTMLElement>(".form-in-progress")) {
				inProgressNode.style.display = "none";
			}
			for (const inProgressNode of this.container.querySelectorAll<HTMLElement>(".form-complete")) {
				inProgressNode.style.display = "";
			}
		} else {
			for (const inProgressNode of this.container.querySelectorAll<HTMLElement>(".form-in-progress")) {
				inProgressNode.style.display = "";
			}
			for (const inProgressNode of this.container.querySelectorAll<HTMLElement>(".form-complete")) {
				inProgressNode.style.display = "none";
			}
			this.container.querySelector<HTMLDialogElement>(`dialog.error-dialog`)?.dispatchEvent(new CustomEvent('open', {bubbles: false}));
		}
	}

	/**
	 * Sets up dialog functionality for error and success messages
	 */
	private wireUpDialogs() {
		for (const dialog of this.container.querySelectorAll<HTMLDialogElement>("dialog.wireable-dialog")) {
			for (const button of dialog.querySelectorAll("button")) {
				button.addEventListener('click', e => {
					e.preventDefault();
					dialog.close();
				});
			}
			dialog.addEventListener('open', () => {
				if (dialog.classList.contains("error-dialog")) {
					dialog.querySelector(".error-list")?.replaceChildren(...(this.getResponseErrorList() as string[]).map(reason => {
						const li = document.createElement("li");
						li.innerText = reason;
						return li;
					}));
				} else if (dialog.classList.contains("success-dialog")) {

				}
				dialog.showModal();
			});
		}
	}
	
	protected abstract getResponseErrorList(): string[];

	protected abstract sendBaseForm(): Promise<unknown>;

	public getBaseURL() {
		return document.querySelector<HTMLSelectElement>(".laravel-endpoint-picker select")?.value ?? this.container.getAttribute("data-base-url") ?? undefined;
	}

	protected async doJsonFetch(url: string, method: string, additionalHeaders: Record<string, string> = {}, body?: RequestInit['body']) {
		const headers: Record<string, string> = {'Accept': 'application/json', ...additionalHeaders};
		if (window.axiosAuth) {
			headers['Authorization'] = `Basic ${btoa(window.axiosAuth.username + ":" + window.axiosAuth.password)}`;
		}
		return (await fetch(new URL(url, this.getBaseURL()).href, {method, headers, body})).json();
	}
}

/**
 * Utility class to handle removable sections within forms
 * Provides functionality to add/remove sections and maintain form state
 */
export class RemovableSection {
	private readonly sectionBefore: Element;
	private usedDefaultRemoval = false;

	public constructor(
		public readonly section: Element,
		private readonly formController: AbstractFormController,
		private readonly beforeRemoval?: () => boolean|undefined
	) {
		this.sectionBefore = section.previousElementSibling!;
	}

	/**
	 * Removes the section from the DOM and clears associated form values
	 */
	remove() {
		if (!this.section.classList.contains("plaudit-site-removed")) {
			this.section.classList.toggle("plaudit-site-removed", true);
			this.usedDefaultRemoval = (!this.beforeRemoval || (this.beforeRemoval() ?? false));
			if (this.usedDefaultRemoval) {
				const handledNames = new Set<string>();
				const event = new Event('formController.remove');
				for (const field of this.section.querySelectorAll<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>("input[name],select[name],textarea")) {
					if (!handledNames.has(field.name)) {
						handledNames.add(field.name);
						this.formController.controller.setValue(field, field.getAttribute("data-model-unselectable-value") ?? '', event, true);
					}
				}
			}
			this.section.remove();
		}
	}

	/**
	 * Reinserts the previously removed section into the DOM
	 * Restores form values if default removal was used
	 */
	insert() {
		if (this.section.classList.contains("plaudit-site-removed")) {
			this.sectionBefore.after(this.section);
			this.section.classList.remove("plaudit-site-removed");
			if (this.usedDefaultRemoval) {
				const handledNames = new Set<string>();
				const event = new Event('formController.insert');
				for (const field of this.section.querySelectorAll<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>("input[name],select[name],textarea")) {
					if (!handledNames.has(field.name)) {
						handledNames.add(field.name);
						this.formController.controller.setValue(field, this.formController.controller.getInputValue(field.name), event, true);
					}
				}
			}
		}
	}

	/**
	 * Toggle the section based on a state value
	 */
	toggle(state: unknown) {
		if (state) {
			this.insert();
		} else {
			this.remove();
		}
		return !!state;
	}

	/**
	 * Factory method to create a removable section if the element exists
	 */
	static createOptional(section: Element|undefined|null, formController: AbstractFormController, beforeRemoval?: () => boolean|undefined): RemovableSection|undefined {
		return section ? new RemovableSection(section, formController, beforeRemoval) : undefined;
	}
}
